import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { secureGet, secureDelete } from '../../config/api';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';

import { PrimaryButton } from '../../components/PrimaryButton';
import LoadingLogo from '../../components/LoadingLogo';
import GenericCard from '../Users/GenericCard';
import PageScroller from '../../components/PageScroller';
import DeletePopup from '../../components/DeletePopup';
import Filter from '../../components/Filter';
import OptionsMenu from '../../components/OptionsMenu';
import ModuleWrapper from '../../components/ModuleWrapper';
import SnackbarNotification from '../../components/SnackbarNotification';

import AddIcon from '../../icons/plusB.svg';
import EditIcon from '../../icons/edit.svg';
import DeleteIcon from '../../icons/trashR.svg';

const Banques = () => {
  const [banks, setBanks] = useState([]);
  const [filteredBanks, setFilteredBanks] = useState([]);
  const [currentPageBanks, setCurrentPageBanks] = useState([]);
  const [pageSize, setPageSize] = useState(30);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [filters, setFilters] = useState({ needle: '' }); 
  const [isDeletePopupVisible, setDeletePopupVisible] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showModule, setShowModule] = useState(false);
  const [selectedBank, setSelectedBank] = useState(null);

  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const { t } = useTranslation();
  const topRef = useRef(null);

  const showSnackbar = (message, severity = 'success', manual = false) => {
    setSnackbar({ open: true, message, severity, manual });
  };

  const abortController = useMemo(() => new AbortController(), []);
  const { signal } = abortController;

  const filterOptions = [
    { name: 'needle', type: 'search' },
  ];

  const paginateData = (data, page, size) => {
    const startIndex = page * size;
    const endIndex = startIndex + size;
    return data.slice(startIndex, endIndex);
  };

  const updatePagination = useCallback((data, page, size) => {
    setTotalPages(Math.ceil((data?.length || 0) / size) || 1);
    setCurrentPageBanks(paginateData(data || [], page, size));
  }, []);

  const fetchBanks = useCallback(async () => {
    setLoading(true);
    try {
      const res = await secureGet('/banks', showSnackbar, { signal });
      const list = res?.data || [];
      setBanks(list);
      setFilteredBanks(list);
      setCurrentPage(0);
      updatePagination(list, 0, pageSize);
    } catch (err) {
      if (err.name !== 'AbortError') console.error('Error fetching banks:', err);
    } finally {
      setLoading(false);
    }
  }, [signal, pageSize, updatePagination]);

  useEffect(() => {
    const ac = new AbortController();
    fetchBanks();
    return () => ac.abort();
  }, [fetchBanks]);

  const applyFilters = useCallback(() => {
    let filtered = [...banks];

    if (filters.needle) {
      const q = filters.needle.toLowerCase();
      filtered = filtered.filter((b) =>
        (b.name || '').toLowerCase().includes(q)
      );
    }

    setFilteredBanks(filtered);
    setCurrentPage(0);
    updatePagination(filtered, 0, pageSize);
  }, [banks, filters, pageSize, updatePagination]);

  useEffect(() => {
    applyFilters();
  }, [filters, banks, applyFilters]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    setCurrentPageBanks(paginateData(filteredBanks, page, pageSize));
    topRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(0);
    updatePagination(filteredBanks, 0, newPageSize);
  };

  const handleFilterChange = (name, value) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const resetFilters = () => {
    setFilters({ needle: '' });
    setFilteredBanks(banks);
    updatePagination(banks, 0, pageSize);
  };

  const handleAddClick = () => {
    setSelectedBank(null);
    setShowModule(true);
  };

  const handleEditClick = (bank) => {
    setSelectedBank(bank);
    setShowModule(true);
  };

  const handleDeleteClick = (bank) => {
    setItemToDelete(bank);
    setDeletePopupVisible(true);
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;
    setDeleting(true);
    try {
      await secureDelete(`/banks/delete/${itemToDelete.id}`, showSnackbar);
      await fetchBanks();
      showSnackbar(t('confirmation.bankDelete') || 'Banque supprimée', 'success');
    } catch (e) {
      console.error('Error deleting bank:', e);
    } finally {
      setDeleting(false);
      setDeletePopupVisible(false);
      setItemToDelete(null);
    }
  };

  const handleModuleClose = (action = 'cancel') => {
    if (action === 'submit') fetchBanks();
    setShowModule(false);
    setSelectedBank(null);
  };

  const getMenuOptions = (bank) => ([
    { label: t('actions.modify'), icon: EditIcon, action: () => handleEditClick(bank) },
    { label: t('actions.delete'), icon: DeleteIcon, color: '#EF476F', action: () => handleDeleteClick(bank) },
  ]);

  return (
    <div ref={topRef}>
      <Helmet>
        <title>Drivnest - {t('titles.banks') || 'Banques'}</title>
      </Helmet>

      <div className="flex items-center mb-[15px] justify-between">
        <h1 className="text-[40px] font-semibold">{t('titles.banks') || 'Banques'}</h1>
        <div className="ml-auto text-[16px]">
          <PrimaryButton
            label={t('primaryButtons.add_bank') || 'Ajouter une banque'}
            icon={AddIcon}
            onClick={handleAddClick}
          />
        </div>
      </div>

      <Filter
        filters={filterOptions}
        onFilterChange={handleFilterChange}
        resetFilters={resetFilters}
        filterValues={filters}
      />

      <div className="relative mt-6">
        {loading && (
          <div className="absolute top-0 left-0 w-full flex justify-center z-10">
            <LoadingLogo />
          </div>
        )}

        {/* Bank Cards */}
        <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
          {!loading && currentPageBanks.map((bank) => (
            <GenericCard
              key={bank.id}
              title={bank.name || ''}
              subtitle={bank.code || ''}
              optionsMenu={
                <div onClick={(e) => e.stopPropagation()}>
                  <OptionsMenu options={getMenuOptions(bank)} />
                </div>
              }
            />
          ))}
        </div>

        {!loading && currentPageBanks.length === 0 && (
          <div className="text-center mt-4">
            <p>{t('placeholder.noResults')}</p>
          </div>
        )}
      </div>

      <PageScroller
        pageNumber={currentPage}
        totalPages={totalPages}
        pageSize={pageSize}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
      />

      {isDeletePopupVisible && (
        <DeletePopup
          open={isDeletePopupVisible}
          onCancel={() => setDeletePopupVisible(false)}
          onConfirm={handleDeleteConfirm}
          loading={deleting}
        />
      )}

      {showModule && (
        <ModuleWrapper
          moduleType="banks"             
          mode={selectedBank ? 'modify' : 'add'}
          onClose={handleModuleClose}
          onSuccess={(msg) => showSnackbar(msg, 'success')}
          initialValues={selectedBank || {}}
        />
      )}

      <SnackbarNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        manual={snackbar.manual}
      />
    </div>
  );
};

export default Banques;
